/**
 * Nomes dos documentos Yjs (seção 4.2).
 *
 *   tree:<projectId>                      árvore de arquivos + metadados
 *   file:<projectId>:<fileId>             conteúdo de um arquivo (Y.Text "content")
 *   suggestions:<projectId>:<fileId>      propostas de mudança (modo sugestão)
 */
export type DocKind = "tree" | "file" | "suggestions";

export interface ParsedDocName {
  kind: DocKind;
  projectId: string;
  fileId: string | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FILE_ID_RE = /^[A-Za-z0-9_-]{8,64}$/;

export const isUuid = (value: string) => UUID_RE.test(value);
export const isFileId = (value: string) => FILE_ID_RE.test(value);

export const treeDoc = (projectId: string) => `tree:${projectId}`;
export const fileDoc = (projectId: string, fileId: string) => `file:${projectId}:${fileId}`;
export const suggestionsDoc = (projectId: string, fileId: string) => `suggestions:${projectId}:${fileId}`;

/** Interpreta e valida um nome de documento. Devolve null para qualquer nome malformado. */
export function parseDocName(name: string): ParsedDocName | null {
  const parts = name.split(":");
  const [kind, projectId, fileId] = parts;
  if (!projectId || !isUuid(projectId)) return null;
  if (kind === "tree" && parts.length === 2) return { kind, projectId, fileId: null };
  if ((kind === "file" || kind === "suggestions") && parts.length === 3 && fileId && isFileId(fileId)) {
    return { kind, projectId, fileId };
  }
  return null;
}

/** Nome do campo Y.Text com o conteúdo do arquivo dentro de `file:*`. */
export const FILE_TEXT_FIELD = "content";
/** Nome do Y.Map com os nós da árvore dentro de `tree:*`. */
export const TREE_NODES_FIELD = "nodes";
/** Nome do Y.Map com as sugestões dentro de `suggestions:*`. */
export const SUGGESTIONS_FIELD = "suggestions";
/** Comentários na linha, no mesmo documento das sugestões (somente leitura para os clientes). */
export const COMMENTS_FIELD = "comments";

const ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

/** Gera um id de arquivo aleatório (16 caracteres, ~95 bits). Funciona no navegador e no Node. */
export function newFileId(): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  let out = "";
  for (const b of bytes) out += ALPHABET[b % ALPHABET.length];
  return out;
}
